'use client';

import { FormEvent, useEffect, useState } from 'react';
import siteMetadata from '@/data/siteMetadata';

type PublicComment = {
  id: number;
  nickname: string;
  content: string;
  created_at: string;
};

function commentEndpoint(slug: string) {
  return `/api/posts/${encodeURIComponent(slug)}/comments/`;
}

function errorMessage(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return '提交失败，请稍后再试。';
  }
  const data = payload as Record<string, unknown>;
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const first = Object.values(data).flat()[0];
  return typeof first === 'string' ? first : '提交失败，请稍后再试。';
}

export default function Comments({ slug }: { slug: string }) {
  const [comments, setComments] = useState<PublicComment[]>([]);
  const [nickname, setNickname] = useState('');
  const [content, setContent] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(commentEndpoint(slug))
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (!cancelled) {
          setComments(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setComments([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const response = await fetch(commentEndpoint(slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ nickname, content, website }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        setError(errorMessage(payload));
        return;
      }
      setContent('');
      setWebsite('');
      setNotice(
        typeof payload?.detail === 'string' ? payload.detail : '已提交，审核通过后才会显示。'
      );
    } catch {
      setError('提交失败，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="text-left">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">评论</h2>
      <div className="mt-6 space-y-5">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">正在加载评论…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">还没有公开的评论。</p>
        ) : (
          comments.map((comment) => (
            <article key={comment.id} className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-medium text-gray-900 dark:text-gray-100">{comment.nickname}</p>
                <time
                  dateTime={comment.created_at}
                  className="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                >
                  {new Date(comment.created_at).toLocaleDateString(siteMetadata.locale, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </time>
              </div>
              <p className="mt-2 text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {comment.content}
              </p>
            </article>
          ))
        )}
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <div className="absolute -left-[9999px] h-0 overflow-hidden" aria-hidden="true">
          <label htmlFor={`comment-website-${slug}`}>Website</label>
          <input
            id={`comment-website-${slug}`}
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor={`comment-nickname-${slug}`} className="mb-1 block text-sm font-medium">
            昵称
          </label>
          <input
            id={`comment-nickname-${slug}`}
            name="nickname"
            required
            maxLength={40}
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label htmlFor={`comment-content-${slug}`} className="mb-1 block text-sm font-medium">
            内容
          </label>
          <textarea
            id={`comment-content-${slug}`}
            name="content"
            required
            minLength={2}
            maxLength={1000}
            rows={4}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {notice && (
          <p className="text-sm text-primary-600 dark:text-primary-400" role="status">
            {notice}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="bg-primary-600 hover:bg-primary-700 rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {submitting ? '提交中…' : '提交评论'}
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400">提交后需要审核，通过后才会公开。</p>
      </form>
    </section>
  );
}
